import React, { useState } from 'react';
import { Container, Typography, Box, Paper, Alert, Button } from '@mui/material';
import { RichTextEditor } from '../components/shared/RichTextEditor';

/**
 * Standalone Test Page for TipTap Rich Text Editor
 * No authentication required - pure editor testing
 * Access at: http://localhost:3000/editor-test
 */
export const TestEditorStandalone: React.FC = () => {
  const [content, setContent] = useState(`
    <h2>LaTeX editor test</h2>
    <p>This paragraph includes inline math: $E = mc^2$ and $x^2 + y^2 = z^2$.</p>
    <p>Display math:</p>
    <p>$$x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}$$</p>
    <p>Try the math toolbar button to add another expression.</p>
  `);

  const [savedContent, setSavedContent] = useState('');
  const [showSaved, setShowSaved] = useState(false);

  const handleSave = () => {
    setSavedContent(content);
    setShowSaved(true);
    setTimeout(() => setShowSaved(false), 3000);
  };

  const handleClear = () => {
    setContent('');
  };

  const handleReset = () => {
    setContent(`
      <h2>LaTeX editor test</h2>
      <p>This paragraph includes inline math: $E = mc^2$ and $x^2 + y^2 = z^2$.</p>
      <p>Display math:</p>
      <p>$$x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}$$</p>
      <p>Try the math toolbar button to add another expression.</p>
    `);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
        <Typography variant="h4" gutterBottom>
          TipTap Rich Text Editor - Standalone Test
        </Typography>
        <Typography variant="body1">
          Test all editor features without authentication. No backend required!
        </Typography>
      </Paper>

      {/* Instructions */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          <strong>How to Test:</strong>
        </Typography>
        <ul style={{ margin: 0, paddingLeft: '20px' }}>
          <li>
            <strong>Rich Text:</strong> Use toolbar buttons to format text
          </li>
          <li>
            <strong>Math Equations:</strong> Click the function button, enter LaTeX (e.g.,{' '}
            <code>E = mc^2</code>)
          </li>
          <li>
            <strong>Images:</strong> Click image icon, paste image URL
          </li>
          <li>
            <strong>Tables:</strong> Click table icon to insert 3x3 table
          </li>
          <li>
            <strong>Save:</strong> Click Save to render the saved HTML below
          </li>
        </ul>
      </Alert>

      {/* LaTeX Examples */}
      <Alert severity="success" sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          <strong>LaTeX Examples to Try:</strong>
        </Typography>
        <Box
          component="pre"
          sx={{
            bgcolor: 'background.paper',
            p: 1,
            borderRadius: 1,
            overflow: 'auto',
            fontSize: '0.875rem',
            fontFamily: 'monospace',
          }}
        >
          {`# Fractions
\\frac{a}{b}

# Quadratic Formula
x = \\frac{-b \\pm \\sqrt{b^2-4ac}}{2a}

# Summation
\\sum_{i=1}^{n} x_i

# Integral
\\int_{a}^{b} f(x) dx

# Physics
E = mc^2
F = ma

# Greek letters
\\alpha + \\beta = \\gamma`}
        </Box>
      </Alert>

      {/* Success Message */}
      {showSaved && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Content saved. The rendered preview is shown below.
        </Alert>
      )}

      {/* Action Buttons */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
        <Button variant="contained" onClick={handleSave}>
          Save Content
        </Button>
        <Button variant="outlined" onClick={handleReset}>
          Reset to Default
        </Button>
        <Button variant="outlined" color="error" onClick={handleClear}>
          Clear All
        </Button>
      </Box>

      {/* Editor */}
      <RichTextEditor
        content={content}
        onChange={setContent}
        placeholder="Start typing here..."
        minHeight={300}
      />

      {/* Saved Content Preview */}
      {savedContent && (
        <Paper sx={{ p: 2, mt: 3, bgcolor: 'grey.50' }}>
          <Typography variant="h6" gutterBottom>
            Saved Content Preview:
          </Typography>
          <Box
            sx={{
              p: 2,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'background.paper',
            }}
          >
            <RichTextEditor
              content={savedContent}
              onChange={() => undefined}
              readOnly
              minHeight={80}
            />
          </Box>
        </Paper>
      )}
    </Container>
  );
};
